import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRISDrawerPayment } from '@shared/models/ContributionsDTO.model';
import {
  MdcSnackbarSeverities,
  MdcSnackbarVariants,
  MdcSnackbarFixedPosition,
  MdcSnackbarFloatingPosition
} from 'ids-wc/dist/types/components';

interface UIState {
  isIbanCheckerDrawerOpen: boolean;
  isPersonalInfoDrawerOpen: boolean;
  isCreateCorrespondenceDrawerOpen: boolean;
  isIRISPaymentDrawerOpen: boolean;
  irisDrawerPayload: IRISDrawerPayment | null;
  isInsuredCreateFormOpen: boolean;
  isInsuredUpdateFormOpen: boolean;
  snackbar: SnackbarState;
  canInsuredFormBeSubmitted: boolean;
}

interface SnackbarMessage {
  id: string;
  messageTitle?: string;
  message: string;
  severity: MdcSnackbarSeverities;
  variant?: MdcSnackbarVariants;
  fixedPosition?: MdcSnackbarFixedPosition;
  floatingPosition?: MdcSnackbarFloatingPosition;
  icon?: string;
  duration?: number;
}

interface SnackbarState {
  messages: SnackbarMessage[];
}

const initialState: UIState = {
  isIbanCheckerDrawerOpen: false,
  isPersonalInfoDrawerOpen: false,
  isIRISPaymentDrawerOpen: false,
  irisDrawerPayload: null,
  isInsuredCreateFormOpen: false,
  isInsuredUpdateFormOpen: false,
  isCreateCorrespondenceDrawerOpen: false,
  snackbar: { messages: [] },
  canInsuredFormBeSubmitted: true
};

const SNACKBAR_DEFAULT_DURATION = 4000;

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openIbanCheckerDrawer: (state) => {
      state.isIbanCheckerDrawerOpen = true;
    },
    closeIbanCheckerDrawer: (state) => {
      state.isIbanCheckerDrawerOpen = false;
    },
    openPersonalInfoDrawer: (state) => {
      state.isPersonalInfoDrawerOpen = true;
    },
    closePersonalInfoDrawer: (state) => {
      state.isPersonalInfoDrawerOpen = false;
    },
    openCreateCorrespondenceDrawer: (state) => {
      state.isCreateCorrespondenceDrawerOpen = true;
    },
    closeCreateCorrespondenceDrawer: (state) => {
      state.isCreateCorrespondenceDrawerOpen = false;
    },
    openIRISPaymentDrawer: (state, action) => {
      state.isIRISPaymentDrawerOpen = true;
      state.irisDrawerPayload = action.payload;
    },
    closeIRISPaymentDrawer: (state) => {
      state.isIRISPaymentDrawerOpen = false;
      state.irisDrawerPayload = null;
    },
    openInsuredCreateForm: (state) => {
      state.isInsuredCreateFormOpen = true;
    },
    closeInsuredCreateForm: (state) => {
      state.isInsuredCreateFormOpen = false;
    },
    openInsuredUpdateForm: (state) => {
      state.isInsuredUpdateFormOpen = true;
    },
    closeInsuredUpdateForm: (state) => {
      state.isInsuredUpdateFormOpen = false;
    },
    showSnackbar: (state, action: PayloadAction<Omit<SnackbarMessage, 'id'>>) => {
      const id = Date.now().toString() + Math.random().toString(36).substring(2, 11);
      const newMessage: SnackbarMessage = { id, duration: SNACKBAR_DEFAULT_DURATION, ...action.payload };

      state.snackbar.messages.push(newMessage);
    },
    hideSnackbar: (state, action: PayloadAction<string>) => {
      state.snackbar.messages = state.snackbar.messages.filter((message) => message.id !== action.payload);
    },
    clearAllSnackbars: (state) => {
      state.snackbar.messages = [];
    },
    enableInsuredUpdateFormSubmit: (state) => {
      state.canInsuredFormBeSubmitted = true;
    },
    disableInsuredUpdateFormSubmit: (state) => {
      state.canInsuredFormBeSubmitted = false;
    }
  }
});

export const UIActions = uiSlice.actions;
export const UIReducer = uiSlice.reducer;
